import React from "react";
import { Box, Typography, Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
    lineHeight: 1.6,
  },
  title: {
    color: "#FE5A97",
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
  },
  subtitle: {
    color: "#FE5A97",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    color: "#fff",
  },
  listItem: {
    marginBottom: theme.spacing(1),
    color: "#fff",
    paddingLeft: theme.spacing(2),
  },
  contactInfo: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  lastUpdated: {
    color: "#ccc",
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
  },
}));

const KYCAMLCompliancePolicy: React.FC = () => {
  const classes = useStyles();

  return (
    <Container maxWidth="md" className={classes.container}>
      <Typography variant="h3" className={classes.title}>
        KYC / AML Compliance Policy
      </Typography>

      <Typography variant="body2" className={classes.lastUpdated}>
        Last Updated: October 17, 2025
      </Typography>

      <Typography variant="body1" className={classes.paragraph}>
        TOPV1 LLC (d/b/a "SugarCribs") maintains a strict Know Your Customer
        (KYC) and Anti-Money Laundering (AML) Compliance Policy to protect our
        users, partners, and the integrity of the SugarCribs platform.
      </Typography>

      <Typography variant="body1" className={classes.paragraph}>
        Our procedures align with applicable U.S. and international regulations,
        including the U.S. Bank Secrecy Act (BSA), FinCEN guidance, FATF
        recommendations, and 18 U.S.C. § 2257 record-keeping requirements for
        adult content.
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        1. Purpose
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        This policy outlines how SugarCribs verifies user and creator
        identities, prevents money laundering, combats fraud, and ensures that
        only eligible adults use our services. We maintain a zero-tolerance
        policy for illegal activity, including exploitation, underage content,
        or sanctions violations.
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        2. Identity Verification (KYC)
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        All users and creators must complete identity verification before
        gaining access to sensitive platform features such as content
        monetization, payouts, or direct messaging.
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Verification may include:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>
          Submission of a government-issued photo ID
        </li>
        <li className={classes.listItem}>
          Facial liveness verification (where legally permitted)
        </li>
        <li className={classes.listItem}>
          Collection of name, date of birth, address, and country
        </li>
        <li className={classes.listItem}>
          Payment account validation and tax information (if applicable)
        </li>
        <li className={classes.listItem}>
          Model releases and 2257 documentation for creators who upload adult
          content
        </li>
      </Box>
      <Typography variant="body1" className={classes.paragraph}>
        KYC is performed through trusted third-party providers under strict
        data-security agreements.
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        3. Prohibited Users
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        SugarCribs does not allow access to:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>
          Minors (under 18 or the age of majority in their jurisdiction)
        </li>
        <li className={classes.listItem}>
          Sanctioned or blacklisted individuals or entities (OFAC, FATF, EU, or
          UN lists)
        </li>
        <li className={classes.listItem}>
          Users in embargoed countries or high-risk jurisdictions
        </li>
        <li className={classes.listItem}>
          Any person who fails, refuses, or falsifies identity verification
        </li>
      </Box>
      <Typography variant="body1" className={classes.paragraph}>
        Accounts that violate these restrictions will be terminated, and
        relevant authorities may be notified.
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        4. Anti-Money Laundering (AML) Measures
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        To comply with AML/CTF regulations, SugarCribs performs:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>
          Transaction monitoring and anomaly detection
        </li>
        <li className={classes.listItem}>Source-of-funds and payout reviews</li>
        <li className={classes.listItem}>
          Periodic account screening and re-verification
        </li>
        <li className={classes.listItem}>
          Suspicious activity reporting (SAR) where required by law
        </li>
      </Box>
      <Typography variant="body1" className={classes.paragraph}>
        The platform prohibits:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>
          Layering or circular transfers between accounts
        </li>
        <li className={classes.listItem}>
          Use of SugarCribs for money-movement or concealment of funds
        </li>
        <li className={classes.listItem}>
          Transactions without legitimate content or engagement basis
        </li>
      </Box>

      <Typography variant="h5" className={classes.subtitle}>
        5. 18 U.S.C. § 2257 Record-Keeping
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Creators are required to maintain documentation confirming that all
        persons depicted in adult content are 18 years or older at the time of
        creation.
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        SugarCribs is not the "primary or secondary producer" of such content.
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Creators must:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>
          Keep 2257 records and custodian details current
        </li>
        <li className={classes.listItem}>
          Provide documentation upon lawful request
        </li>
        <li className={classes.listItem}>
          See our 2257 Compliance Statement for more information
        </li>
      </Box>

      <Typography variant="h5" className={classes.subtitle}>
        6. Data Security
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        All verification and compliance data are processed in accordance with
        our Privacy Policy.
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Sensitive information is encrypted, stored securely, and retained only
        as long as legally necessary for compliance, fraud prevention, and audit
        purposes.
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        7. Reporting Suspicious Activity
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        If you become aware of illegal or suspicious conduct on SugarCribs,
        report it immediately at:
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        📧 legal@sugarcribs.com
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        8. Updates
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        This Policy may be updated periodically to reflect changes in law or
        industry standards. Material changes will be communicated through the
        Platform and this page.
      </Typography>

      <Box className={classes.contactInfo}>
        <Typography
          variant="h6"
          style={{ color: "#FE5A97", marginBottom: "16px" }}
        >
          Contact Information
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>TOPV1 LLC (d/b/a "SugarCribs")</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          732 S. 6th Street, Suite N, Las Vegas, NV 89101
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Email: support@sugarcribs.com
        </Typography>
      </Box>
    </Container>
  );
};

export default KYCAMLCompliancePolicy;
